<template>
  <div style="position: relative;">
    <AutoComplete
        :title="selectedItem ? selectedItem.label + ' ' + selectedItem.hookId : ''"
        ref="autoCompleteRef"
        dropdown
        @focus="highlightText"
        v-model="selectedItem"
        @itemSelect='handleContentClick'
        :suggestions="filteredVariables"
        @complete="search"
        optionLabel="label"
        optionGroupLabel="label"
        optionGroupChildren="items"
        placeholder="Search Value Model"
        class="autocomplete-custom"
        :disabled="disabled"
        :inputStyle="{ width: '250px' }"
        dropdownClass="auto-dropdown"
    >
      <template #optiongroup="slotProps">
        <div class="labels flex align-items-center">
          <div>{{ slotProps.option.label }}</div>
        </div>
      </template>
      <template #option="slotProps">
        <div class="flex items-center">
          <span style="font-size: 15px; display: inline-block; color: #333333;">{{ slotProps.option.label }}</span>
          <span style="float: right; color: #aaaaaa; font-size: 14px; padding-left: 20px; padding-right: 5px;">{{ slotProps.option.hookId }}</span>
        </div>
      </template>
    </AutoComplete>
  </div>
</template>

<script setup lang="ts">
import {ref, onMounted, watch, computed, nextTick} from "vue";
import {useGivingTreeStore, useMainStore, useWidgetStore} from "@/store";
import {storeToRefs} from "pinia";

import Fuse from "fuse.js";

const widgetStore = useWidgetStore()
const givingTreeStore = useGivingTreeStore()
const { tableCellHookId } = storeToRefs(useMainStore())
const { selectedWidget } = storeToRefs(widgetStore)
const { globalCalculations, metaByHookId,  variablesByHookId, valueDriversByHookId, priceComponentsByHookId, benefits } = storeToRefs(givingTreeStore)
const { selectedTableCells: selectedCells } = storeToRefs(useMainStore())

const props = defineProps({
  refVmDataVisible: {
    type: Object
  },
  csvSettings: {
    type: Boolean,
    default: false
  },
  csvLabel: {
    type: String
  },
  chartData: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean
  },
  wizard: {
    type: Boolean,
    default: false
  },
  wizardHookId: {
    type: String,
    default: null
  },
  selectedHookId: {
    type: String,
    default: null
  },
  ruleSet: {
    type: Boolean,
    default: false
  },
  ruleSetHookId: {
    type: String,
    default: null
  }
})

const selectedItem = ref()
const filteredVariables = ref()
const groupedVariables = ref([])

const emit = defineEmits(['itemSelected']);

watch(() => props.selectedHookId, () => {
    findItemByHookId(props.selectedHookId)
})

const autoCompleteRef = ref(null);

const highlightText = () => {
  nextTick(() => {
    const inputEl = autoCompleteRef.value?.$el?.querySelector('input');
    if (inputEl) {
      inputEl.select();
    }
  });
};

onMounted(() => {
  transformedData()


  if (props.csvSettings && !isEmptyObject(selectedWidget.value.csv)) {
    const csvColumn = selectedWidget.value.csv.otherColumns.find(column => column.label === props.csvLabel)
    findItemByHookId(csvColumn.hookId)
  } else if (props.wizard && props.wizardHookId) {
    findItemByHookId(props.wizardHookId)
  } else if (props.ruleSet || props.ruleSetHookId) {
    findItemByHookId(props.ruleSetHookId)
  } else if (selectedWidget.value.hookId) {
    findItemByHookId(selectedWidget.value.hookId)
  } else {
    selectedItem.value = null;
  }
})

const selectedCell = computed(() => {
  if (selectedCells.value.length) {
    const selectedCell = selectedCells.value[0]
    const rowIndex = +selectedCell.split('_')[0]
    const colIndex = +selectedCell.split('_')[1]
    if (selectedWidget.value?.data && selectedWidget.value?.data.length > rowIndex &&
        selectedWidget.value?.data[rowIndex].length > colIndex
    ) {
      return selectedWidget.value?.data[rowIndex][colIndex]
    }
  }
  return null
})

watch(selectedCell, () => {
  const hookId = selectedCell.value && selectedCell.value?.hookId
  if (hookId) {
    findItemByHookId(hookId)
  }
  else {
    selectedItem.value = null
  }

})

watch(selectedWidget, () => {
  // if (!props.wizard) {
  //   findItemByHookId(selectedWidget.value.hookId)
  // }
  if (props.csvSettings && !isEmptyObject(selectedWidget.value.csv)) {
    const csvColumn = selectedWidget.value.csv.otherColumns.find(column => column.label === props.csvLabel)
    findItemByHookId(csvColumn.hookId)
  } else if (props.wizard && props.wizardHookId) {
    findItemByHookId(props.wizardHookId)
  } else if (props.ruleSet && props.ruleSetHookId) {
    findItemByHookId(props.ruleSetHookId)
  } else if (selectedWidget.value.hookId) {
    findItemByHookId(selectedWidget.value.hookId)
  } else {
    selectedItem.value = null;
  }
})

// using a watch to pre-populate autocomplete in table style panel
watch(tableCellHookId, (newVal) => {
  findItemByHookId(newVal)
})

const isEmptyObject = (obj: any) => Object.entries(obj).length === 0

const handleContentClick = (event: any) => {
  if (!props.csvSettings && !props.chartData && !props.wizard && !props.ruleSet) {
    const item = event.value

    if (selectedCell.value) {
      selectedCell.value.hookId = item.hookId
      if (selectedCell.value.text === '') {
        selectedCell.value.text = 'Add Text'
      }
    }
    else {
      const prevText = selectedWidget.value.hookId ? givingTreeStore.getWidgetValue(selectedWidget.value.hookId)?.name : null

      if (prevText && selectedWidget.value.text.indexOf(prevText) > -1) {
          // this might be a good place for AI replace
          selectedWidget.value.text = selectedWidget.value.text.replace(prevText, item.label)
      }
      else {
        selectedWidget.value.text = item.label
      }

      selectedWidget.value.hookId = item.hookId
    }

    props.refVmDataVisible ? props.refVmDataVisible.hide() : ''
  } else {
    emit('itemSelected', event)
  }
}

const findItemByHookId = (hookId: string) => {
  selectedItem.value = null
  groupedVariables.value.forEach(category => {
    const foundItem = category.items.find(item => item.hookId === hookId);
    if (foundItem && !selectedItem.value) {
      selectedItem.value = foundItem;
    }
  })
}

const search = (event: any) => {
  let newFilteredVariables: any = [];

  if (event.query.trim() === '') {
    groupedVariables.value.forEach((group) => {
      newFilteredVariables.push({...group, items: group.items});
    });
  }
  else {
    const query = event.query.toLowerCase();

    const fuseOptions = {
      keys: ['label', 'hookId'],
      includeMatches: true,
      threshold: 0.1,
      ignoreLocation: true,
      includeScore: true,
      findAllMatches: true,
      useExtendedSearch: true
    };

    // Flatten the group structure for Fuse to work effectively
    const flattenedGroups = groupedVariables.value.flatMap(group =>
        group.items.map(item => ({
          ...item,
          groupLabel: group.label
        }))
    );

    const fuse = new Fuse(flattenedGroups, fuseOptions);
    const fuseResults = fuse.search(query);
    console.log('fuse', fuseResults);

    // Rebuild the grouped structure from search results
    const groupedResults = fuseResults.reduce((acc, result) => {
      const item = result.item;
      const groupLabel = item.groupLabel;

      let group = acc.find(g => g.label === groupLabel);
      if (!group) {
        group = { label: groupLabel, items: [] };
        acc.push(group);
      }

      group.items.push(item);
      return acc;
    }, []);

    newFilteredVariables = groupedResults;
  }

  filteredVariables.value = newFilteredVariables;
};


const transformedData = () => {
  const meta = metaByHookId.value
  const variables = variablesByHookId.value
  const valueDrivers = valueDriversByHookId.value
  const priceComponents = priceComponentsByHookId.value
  const vmBenefits = benefits.value

  const transformedDataArray = []

  const getMetaArray = (meta) => {
    return Object.keys(meta).map((metaHookId) => {
      const metaItem = meta[metaHookId].name
      return { label: metaItem, value: metaItem, hookId: metaHookId }
    })
  }

  const getVariableArray = (variables) => {
    return Object.keys(variables)
        .filter(variableHookId => !variables[variableHookId].isChoice)
        .map((variableHookId) => {
          const variable = variables[variableHookId].node.variable.Name
          const isReadOnly = variables[variableHookId].readOnly
          const hasFormula = variables[variableHookId].hasFormula
          return { label: variable, value: variable, hookId: variableHookId, readOnly: isReadOnly, hasFormula: hasFormula }
        })
  }

  const getChoiceArray = (variables) => {
    return Object.keys(variables)
        .filter(variableHookId => variables[variableHookId].isChoice)
        .map((variableHookId) => {
          const choice = variables[variableHookId].name;
          return { label: choice, value: choice, hookId: variableHookId }
        })
  }

  const getValueDriverArray = (valueDrivers) => {
    return Object.keys(valueDrivers).map((valueDriverHookId) => {
      if (valueDrivers[valueDriverHookId].enabled) {
        const valueDriver = valueDrivers[valueDriverHookId].node.valueDriver.Name
        const hasFormula = valueDrivers[valueDriverHookId].hasFormula
        return { label: valueDriver, value: valueDriver, hookId: valueDriverHookId, hasFormula: hasFormula }
      }
    }).filter(valueDriver => valueDriver !== undefined)
  }

  const getPriceArray = (priceComponents, hookFilter) => {
    return Object.keys(priceComponents).filter(hookFilter).map((hookId) => {
      if (priceComponents[hookId].enabled) {
        const price = priceComponents[hookId].node.variable.Name
        const hasFormula = priceComponents[hookId].hasFormula
        return { label: price, value: price, hookId: hookId, hasFormula: hasFormula }
      }
    }).filter(price => price !== undefined)
  }

  const getGlobalCalcsArray = (globalCalculations) => {
    return Object.entries(globalCalculations.value).map(([key]) => {
      const formattedKey = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (str) => str.toUpperCase())
      return {
        label: formattedKey,
        value: formattedKey,
        hookId: key
      }
    })
  }

  const getBenefitsArray = (vmBenefits) => {
    return Object.keys(vmBenefits).map((guid) => {
      const benefitItem = vmBenefits[guid].name
      return { label: benefitItem, value: benefitItem, hookId: guid }
    })
  }

  transformedDataArray.push({
    label: 'Value Model',
    items: getMetaArray(meta),
  })

  transformedDataArray.push({
    label: 'Global Calculations',
    items: getGlobalCalcsArray(globalCalculations),
  })

  transformedDataArray.push({
    label: 'Value Drivers',
    items: getValueDriverArray(valueDrivers),
  })

  transformedDataArray.push({
    label: 'Variables',
    items: getVariableArray(variables),
  })

  transformedDataArray.push({
    label: 'Our Price Components',
    items: getPriceArray(priceComponents, hookId => hookId.includes('cpc')),
  })

  transformedDataArray.push({
    label: 'Competitor Price Components',
    items: getPriceArray(priceComponents, hookId => hookId.includes('opc')),
  })

  transformedDataArray.push({
    label: 'Choice Variables',
    items: getChoiceArray(variables),
  })

  transformedDataArray.push({
    label: 'Benefits',
    items: getBenefitsArray(vmBenefits),
  })

  groupedVariables.value = transformedDataArray
}

</script>

